// src/components/strumenti/CalcoloScala.js
import React, { useState } from "react";
import { RefreshCw, Printer } from "lucide-react";
import { leggiNumero } from "../../utils/numeriStrumenti";

const inp = { padding:"9px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

export default function CalcoloScala() {
  const [denominazione, setDenominazione] = useState("");
  const [note, setNote] = useState("");
  const [altezzaTotale, setAltezzaTotale] = useState("");
  const [alzataDesiderata, setAlzataDesiderata] = useState("17");

  const H = (leggiNumero(altezzaTotale)||0)*100; // m -> cm
  const alzataTarget = leggiNumero(alzataDesiderata)||17;

  const numAlzate = H>0 ? Math.round(H/alzataTarget) : null;
  const alzataReale = numAlzate ? H/numAlzate : null;
  // Regola pratica del passo: 2×alzata + pedata = 62-64 cm (uso 63 come riferimento medio)
  const pedataConsigliata = alzataReale ? 63-2*alzataReale : null;
  const numPedate = numAlzate ? numAlzate-1 : null;
  const ingombroOrizzontale = (pedataConsigliata && numPedate) ? pedataConsigliata*numPedate : null;

  const passoOk = pedataConsigliata!==null && pedataConsigliata>=25 && pedataConsigliata<=32;

  const stampaPDF = () => {
    const win = window.open("", "_blank");
    const oggi = new Date().toLocaleDateString("it-IT", { day:"numeric", month:"long", year:"numeric" });
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Calcolo Scala — ${denominazione}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:14mm;}
      h1{font-size:18px;color:#15803d;margin-bottom:2px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;margin-bottom:14px;}
      td,th{border:1px solid #e5e7eb;padding:6px 10px;text-align:left;font-size:12px;}
      th{background:#f9fafb;width:45%;color:#374151;}
      .risultato{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:16px;text-align:center;margin-bottom:14px;}
      .risultato .val{font-size:24px;font-weight:800;color:#15803d;}
    </style></head><body>
      <h1>🪜 Calcolo Scala</h1>
      <div class="sub">Nexiora — Strumenti Tecnici · Generato il ${oggi}</div>
      <table>
        <tr><th>Denominazione</th><td>${denominazione || "—"}</td></tr>
        <tr><th>Note</th><td>${note || "—"}</td></tr>
      </table>
      <table>
        <tr><th>Altezza totale da superare</th><td>${altezzaTotale} m</td></tr>
        <tr><th>Alzata desiderata</th><td>${alzataDesiderata} cm</td></tr>
      </table>
      <div class="risultato">
        <div style="display:flex;justify-content:space-around;margin-bottom:10px;font-size:11px;color:#6b7280;">
          <div><div>N° alzate</div><div style="font-weight:700;color:#374151;font-size:15px;">${numAlzate ?? "—"}</div></div>
          <div><div>Alzata reale</div><div style="font-weight:700;color:#374151;font-size:15px;">${alzataReale ? alzataReale.toFixed(1) : "—"} cm</div></div>
        </div>
        <div style="border-top:1px solid #bbf7d0;padding-top:10px;">
          <div style="font-size:11px;color:#9ca3af;">Pedata consigliata (regola 2a + p ≈ 63 cm)</div>
          <div class="val">${pedataConsigliata ? pedataConsigliata.toFixed(1) : "—"} cm</div>
        </div>
        ${ingombroOrizzontale!==null ? `<div style="border-top:1px solid #bbf7d0;padding-top:10px;margin-top:10px;font-size:12px;color:#374151;">Ingombro orizzontale rampa: <strong>${(ingombroOrizzontale/100).toFixed(2)} m</strong></div>` : ""}
      </div>
      <div style="font-size:10px;color:#9ca3af;">Regola pratica di comfort — non sostituisce le verifiche normative su scale (D.M. 236/89, sicurezza sul lavoro, ecc.).</div>
      
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div>
      <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:12, marginBottom:16 }}>
        <div style={{ marginBottom:8 }}>
          <label style={lbl}>Denominazione (necessaria per la stampa)</label>
          <input value={denominazione} onChange={e=>setDenominazione(e.target.value)} style={inp} placeholder="Es. Scala accesso primo piano - Cantiere Rossi"/>
        </div>
        <div>
          <label style={lbl}>Note (facoltativo)</label>
          <input value={note} onChange={e=>setNote(e.target.value)} style={inp}/>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        <div>
          <label style={lbl}>Altezza totale da superare (m)</label>
          <input value={altezzaTotale} onChange={e=>setAltezzaTotale(e.target.value)} style={inp} placeholder="Es. 2.80"/>
        </div>
        <div>
          <label style={lbl}>Alzata desiderata (cm)</label>
          <input value={alzataDesiderata} onChange={e=>setAlzataDesiderata(e.target.value)} style={inp}/>
        </div>
      </div>

      <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-around", marginBottom:10 }}>
          <div style={{textAlign:"center"}}>
            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>N° alzate</div>
            <div style={{ fontSize:20, fontWeight:800, color:"#15803d" }}>{numAlzate ?? "—"}</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>Alzata reale</div>
            <div style={{ fontSize:20, fontWeight:800, color:"#15803d" }}>{alzataReale ? alzataReale.toFixed(1) : "—"} cm</div>
          </div>
        </div>
        <div style={{ textAlign:"center", borderTop:"1px solid #bbf7d0", paddingTop:10 }}>
          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginBottom:2 }}>Pedata consigliata (regola 2a + p ≈ 63 cm)</div>
          <div style={{ fontSize:22, fontWeight:800, color: passoOk ? "#15803d" : "#d97706" }}>{pedataConsigliata ? pedataConsigliata.toFixed(1) : "—"} cm</div>
          {pedataConsigliata!==null && !passoOk && (
            <div style={{ fontSize:11, color:"#92400e", marginTop:4 }}>Fuori dall'intervallo comodo tipico (25-32 cm) — valutare un'alzata diversa</div>
          )}
        </div>
        {ingombroOrizzontale!==null && (
          <div style={{ textAlign:"center", borderTop:"1px solid #bbf7d0", paddingTop:10, marginTop:10 }}>
            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>Ingombro orizzontale (rampa)</div>
            <div style={{ fontSize:16, fontWeight:700, color:"#374151" }}>{(ingombroOrizzontale/100).toFixed(2)} m</div>
          </div>
        )}
      </div>
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>
        Regola pratica di comfort — non sostituisce le verifiche normative su scale (D.M. 236/89, sicurezza sul lavoro, ecc.).
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:12 }}>
        <button onClick={()=>{setAltezzaTotale("");setAlzataDesiderata("17");setDenominazione("");setNote("");}}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
          <RefreshCw size={12}/> Azzera
        </button>
        <button onClick={stampaPDF} disabled={!denominazione.trim()}
          title={!denominazione.trim() ? "Inserisci la denominazione per stampare" : "Stampa PDF"}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
            color: denominazione.trim() ? "#15803d" : "#bbf7d0",
            background: denominazione.trim() ? "#f0fdf4" : "#f9fafb",
            border:`1px solid ${denominazione.trim() ? "#bbf7d0" : "#e5e7eb"}`,
            borderRadius:8, padding:"7px 14px", cursor: denominazione.trim() ? "pointer" : "not-allowed" }}>
          <Printer size={13}/> Stampa PDF
        </button>
      </div>
    </div>
  );
}
